import { useGetEmployeeProfiles, useHasPermission } from '@shared/hooks';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { CompanyPolicyMiniResponse } from '@interfaces/models';
import { BadgeColor, Badge, toast } from '@ui/components';
import { assignEmployeeProfiles } from '@api/employees';
import { BadgeMultiselect } from '@shared/components';
import { useState } from 'react';
import { t } from 'i18next';

export const UpdateEmployeeProfileSelect = ({
  employeeId,
  employeeProfiles,
}: {
  employeeProfiles: CompanyPolicyMiniResponse[];
  employeeId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasEditPermission = useHasPermission('EMPLOYEES_EDIT_EMPLOYEE', 'WRITE');
  const { employeeProfilesOptions } = useGetEmployeeProfiles();
  const queryClient = useQueryClient();

  const { mutate: updateEmployeeProfilesMutation } = useMutation({
    mutationFn: ({
      employeeId,
      employeeProfiles,
    }: {
      employeeProfiles: string[];
      employeeId: string;
    }) => assignEmployeeProfiles(employeeId, employeeProfiles),
    onSuccess: () => {
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      toast.success(t('EMPLOYEES.TABLE_SUCCESS_UPDATE_EMPLOYEE_PROFILES'));
    },
    onError: () => {
      toast.error(t('EMPLOYEES.TABLE_ERROR_UPDATE_EMPLOYEE_PROFILES'));
    },
  });

  const handleUpdateEmployeeProfiles = (employeeId: string, employeeProfiles: string[]) => {
    if (employeeProfiles.length === 0) {
      return toast.warning(t('EMPLOYEES.TABLE_ERROR_SELECT_EMPLOYEE_PROFILE'));
    }
    updateEmployeeProfilesMutation({ employeeId, employeeProfiles });
  };

  const employeeProfileCount = employeeProfiles.length;
  const profileOptions = employeeProfilesOptions
    .map((profile) => ({ ...profile, disabled: profile.isDefault }))
    .sort((a, b) => (a.isDefault ? 1 : b.isDefault ? -1 : 0));

  const firstProfile = employeeProfiles.sort(
    (a, b) =>
      profileOptions.findIndex((profile) => profile.value === a.id) -
      profileOptions.findIndex((profile) => profile.value === b.id),
  )[0];

  return (
    <BadgeMultiselect
      searchPlaceholder={t('EMPLOYEES.TABLE_SEARCH_OR_SELECT_EMPLOYEE_PROFILE')}
      onSave={(profiles) => handleUpdateEmployeeProfiles(employeeId, profiles)}
      buttonLabel={t('EMPLOYEES.TABLE_ACCEPT_EMPLOYEE_PROFILE')}
      value={employeeProfiles.map((profile) => profile.id)}
      disabled={!hasEditPermission}
      options={profileOptions}
      setIsOpen={setIsOpen}
      isOpen={isOpen}
    >
      <div className="flex items-center gap-1 whitespace-nowrap cursor-pointer">
        {isOpen}
        <Badge
          variant={firstProfile?.color as BadgeColor}
          className="[&>svg]:size-4"
          size="large"
          outlined
        >
          {firstProfile?.name}
        </Badge>
        {employeeProfileCount > 1 && (
          <Badge size="large" outlined icon>
            +{employeeProfileCount - 1}
          </Badge>
        )}
      </div>
    </BadgeMultiselect>
  );
};
